import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Overline, Reveal } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { useSEO } from '../lib/seo';
import { INSIGHTS } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

const InsightsHero = () => (
    <section className="relative overflow-hidden pt-44 pb-20 sm:pb-24" data-testid="insights-hero">
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="pointer-events-none absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                <Overline>Insights</Overline>
            </motion.div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
                {['Notes from', 'prepared side.'].map((line, i) => (
                    <span key={line} className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: '110%' }}
                            animate={{ y: '0%' }}
                            transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                        >
                            {i === 1 ? <>the <span className="text-deepteal">prepared side.</span></> : line}
                        </motion.span>
                    </span>
                ))}
            </h1>
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
                Short, practical writing on continuity, recovery, and resilience — from people who deliver it, not comment on it.
            </motion.p>
        </div>
    </section>
);

const InsightCard = ({ article }) => (
    <Reveal>
        <Link
            to={`/insights/${article.id}`}
            data-testid={`insight-card-${article.id}`}
            className="group block rounded-2xl border border-slate-700 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-deepteal/40 hover:shadow-md sm:p-9"
        >
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="flex items-center gap-3">
                        <span className="rounded-full border border-deepteal/30 bg-deepteal/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-deepteal">
                            {article.category}
                        </span>
                        <span className="font-mono text-[11px] text-slate-400">{article.readTime}</span>
                    </div>
                    <h2 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-50 text-balance">
                        {article.title}
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base leading-relaxed text-slate-300">{article.excerpt}</p>
                </div>
                <span className="mt-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-700 text-slate-300 transition-colors group-hover:border-deepteal/40 group-hover:text-deepteal">
                    <ArrowUpRight size={16} />
                </span>
            </div>
            <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-deepteal">Read the insight</span>
        </Link>
    </Reveal>
);

export default function Insights() {
    useSEO(
        'Insights — Business Continuity & Resilience Thinking | Planmodo',
        'Practical writing on business continuity, disaster recovery, tabletop exercises and operational resilience — from practitioners who deliver it.'
    );

    return (
        <>
            <InsightsHero />
            <section className="pb-20 sm:pb-28" data-testid="insights-list">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="space-y-6">
                        {INSIGHTS.map((article) => (
                            <InsightCard key={article.id} article={article} />
                        ))}
                    </div>
                </div>
            </section>
            <CTASection
                title="Prefer a conversation to an article?"
                text="Bring us your specific situation. A thirty-minute consultation with a practitioner is worth a library of generic guidance."
            />
        </>
    );
}
